import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import DropboxStateOverviewTab from "@/components/DropboxStateOverviewTab";
import DropboxBlockPerformanceTab from "@/components/DropboxBlockPerformanceTab";
import DropboxSchoolDeepDiveTab from "@/components/DropboxSchoolDeepDiveTab";
import DropboxRiskGovernanceTab from "@/components/DropboxRiskGovernanceTab";
import DropboxInsightsTab from "@/components/DropboxInsightsTab";
import "@/components/DropboxStateOverviewTab.css";
import "@/components/DropboxBlockPerformanceTab.css";
import "@/components/DropboxSchoolDeepDiveTab.css";
import "@/components/DropboxRiskGovernanceTab.css";
import "@/components/DropboxInsightsTab.css";
import { 
  FileText, 
  AlertTriangle, 
  RefreshCw,
  Upload,
  BarChart3,
  ClipboardCheck,
  LayoutGrid,
  Lightbulb,
  LineChart,
  ShieldAlert
} from "lucide-react";
import { toast } from "sonner";
import ExportPanel from "@/components/ExportPanel";
import DashboardPageHeader from "@/components/DashboardPageHeader";

import { getBackendUrl } from "@/lib/backend";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const TAB_ITEMS = [
  { id: "overview", label: "State Overview", icon: BarChart3 },
  { id: "blocks", label: "Block Performance", icon: LayoutGrid },
  { id: "schools", label: "School Deep Dive", icon: ClipboardCheck },
  { id: "governance", label: "Risk & Governance", icon: AlertTriangle },
  { id: "insights", label: "Insights", icon: Lightbulb },
];

const DropboxDashboard = () => {
  const { scope } = useScope();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [categoryData, setCategoryData] = useState([]);
  const [blockData, setBlockData] = useState([]);
  const [topSchools, setTopSchools] = useState([]);
  const [dataQuality, setDataQuality] = useState(null);
  const [transitionData, setTransitionData] = useState([]);
  const [dropoutHotspots, setDropoutHotspots] = useState([]);
  const [managementWiseData, setManagementWiseData] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        categoryRes,
        blockRes,
        topRes,
        qualityRes,
        transitionRes,
        hotspotRes,
        managementRes
      ] = await Promise.all([
        axios.get(`${API}/dropbox/overview`),
        axios.get(`${API}/dropbox/category-distribution`),
        axios.get(`${API}/dropbox/block-wise`),
        axios.get(`${API}/dropbox/top-schools`),
        axios.get(`${API}/dropbox/data-quality`),
        axios.get(`${API}/dropbox/transition-analysis`),
        axios.get(`${API}/dropbox/dropout-hotspots`),
        axios.get(`${API}/dropbox/management-wise`)
      ]);
      
      setOverview(overviewRes.data);
      setCategoryData(categoryRes.data);
      setBlockData(blockRes.data);
      setTopSchools(topRes.data);
      setDataQuality(qualityRes.data);
      setTransitionData(transitionRes.data);
      setDropoutHotspots(hotspotRes.data);
      setManagementWiseData(Array.isArray(managementRes.data) ? managementRes.data : []);
    } catch (error) {
      console.error("Error fetching Dropbox data:", error);
      toast.error("Failed to load dropbox remarks data");
    } finally {
      setLoading(false);
    }
  }, [scope.version]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImport = async () => {
    const url = "https://customer-assets.emergentagent.com/job_7d6cbc1e-b567-4fbc-af84-06fa5107bbd4/artifacts/lr06jctk_5.%20Dropbox%20Remarks%20Statistics%20-%20School%20Wise%20-%20Real%20Time%20%28State%29%20MAHARASHTRA.xlsx";
    
    setImporting(true);
    try {
      await axios.post(`${API}/dropbox/import?url=${encodeURIComponent(url)}`);
      toast.success("Dropbox Remarks data import started!");
      setTimeout(() => {
        fetchData();
        setImporting(false);
      }, 5000);
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner" />
      </div>
    );
  }

  const hasData = overview && overview.total_schools > 0;

  const scopeLabel =
    scope.schoolName?.trim() ||
    scope.blockName?.trim() ||
    scope.districtName?.trim() ||
    "Pune District";

  const headerCopy = {
    overview: {
      eyebrow: `State overview · Real-time remarks · ${scopeLabel}`,
      subtitle:
        "A cleaner command-center view for dropout remarks, import quality, duplicate entries and block-level operational load. The redesign reduces chart clutter and moves users from summary → diagnosis → action.",
      icon: BarChart3,
    },
    blocks: {
      eyebrow: `Block Performance · ${scopeLabel} · Real-time`,
      subtitle:
        "Block-wise performance view redesigned for faster diagnosis: volume leaders, intensity outliers, dropout pressure, data-error risk and category mix are separated into clear decision zones.",
      icon: LineChart,
    },
    schools: {
      eyebrow: `School-wise remarks · Deep dive · ${scopeLabel}`,
      subtitle:
        "School-level diagnostics for high-volume remarks, data quality exceptions, dropout signals and student transition outcomes.",
      icon: ClipboardCheck,
    },
    governance: {
      eyebrow: `Risk & Governance · ${scopeLabel} · Real-time`,
      subtitle:
        "School-wise remarks statistics, risk monitoring and governance controls for dropout, error and pending import follow-up across Pune District.",
      icon: ShieldAlert,
    },
    insights: {
      eyebrow: `Mistral AI · Executive insight layer · ${scopeLabel}`,
      subtitle:
        "School-wise remarks statistics, risk signals, AI-generated root causes and action recommendations for Pune District.",
      icon: Lightbulb,
    },
  };

  const copy = headerCopy[activeTab] || headerCopy.overview;
  const EyebrowIcon = copy.icon;

  return (
    <div className="dbx-dashboard space-y-6 animate-fade-in" data-testid="dropbox-dashboard">
      <DashboardPageHeader
        title="Dropbox Remarks Analytics"
        subtitle={
          hasData
            ? copy.subtitle
            : "School-wise Remarks Statistics • Real-Time • Pune District"
        }
        eyebrow={
          hasData ? (
            <>
              <EyebrowIcon size={14} />
              {copy.eyebrow}
            </>
          ) : null
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleImport}
              disabled={importing}
              data-testid="import-dropbox-btn"
            >
              {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? "Importing..." : "Import Data"}
            </Button>
            <ExportPanel dashboardName="dropbox" dashboardTitle="Dropbox Remarks" />
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </>
        }
      />

      {!hasData ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Dropbox Remarks Data Available</h3>
            <p className="text-slate-500 mb-4">Click "Import Data" to load the Dropbox Remarks Excel file</p>
            <Button onClick={handleImport} disabled={importing} data-testid="import-dropbox-empty-btn">
              {importing ? "Importing..." : "Import Dropbox Data"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Dashboard Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="dbx-tabs-wrap" aria-label="Dropbox dashboard sections">
              <nav className="dbx-tabs">
                {TAB_ITEMS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={`dbx-tab ${activeTab === id ? "active" : ""}`}
                    onClick={() => setActiveTab(id)}
                    data-testid={`tab-${id}`}
                  >
                    <Icon />
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            <TabsContent value="overview" className="mt-0">
              <DropboxStateOverviewTab
                overview={overview}
                categoryData={categoryData}
                blockData={blockData}
                dataQuality={dataQuality}
                managementWiseData={managementWiseData}
                onViewBlocks={() => setActiveTab("blocks")}
                onViewActions={() => {
                  const el = document.getElementById("dbx-actions");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              />
            </TabsContent>

            <TabsContent value="blocks" className="mt-0">
              <DropboxBlockPerformanceTab overview={overview} blockData={blockData} />
            </TabsContent>

            <TabsContent value="schools" className="mt-0">
              <DropboxSchoolDeepDiveTab topSchools={topSchools} transitionData={transitionData} />
            </TabsContent>

            {/* DASHBOARD 4: RISK & GOVERNANCE */}
            <TabsContent value="governance" className="mt-0">
              <DropboxRiskGovernanceTab
                overview={overview}
                dataQuality={dataQuality}
                dropoutHotspots={dropoutHotspots}
              />
            </TabsContent>
            <TabsContent value="insights" className="mt-0">
              <DropboxInsightsTab overview={overview} dataQuality={dataQuality} blockData={blockData} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default DropboxDashboard;
